import { History } from 'lucide-react';
import { Link } from 'react-router';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaEmpresa } from '@/lib/auth/permissoes';
import { useEstadoCaptura } from '@/lib/captura/api';
import { BuscarAgora } from '@/lib/captura/buscar-agora';
import { UltimaProxima } from '@/lib/captura/quando';
import {
  AcaoSugerida,
  AvisoOutroConsumidor,
  SituacaoCaptura,
} from '@/lib/captura/situacao-captura';
import { mensagemDeErro } from '@/lib/erros';
import { Skeleton } from '@/ui/primitives/skeleton';

/**
 * Linha de situação no topo da aba (a mesma do painel /captura): como está, quando buscou e vai
 * buscar, o "Buscar agora" e o caminho para o histórico. Só para quem lê documentos da empresa.
 */
export function SituacaoAtual({ empresaId }: { empresaId: string }) {
  const usuario = useUsuario();
  const le = podeNaEmpresa(usuario, empresaId, 'documentos.ler');
  const consulta = useEstadoCaptura(empresaId, le);
  if (!le) return null;
  if (consulta.isError)
    return (
      <p role="alert" className="text-sm text-perigo">
        A situação da captura não carregou: {mensagemDeErro(consulta.error)}
      </p>
    );
  if (!consulta.data) return <Skeleton className="h-20 rounded-cartao" />;
  const estado = consulta.data;
  return (
    <section
      aria-label="Situação da captura"
      className="grid gap-3 rounded-cartao border border-borda bg-superficie px-4 py-3 shadow-baixa"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="grid min-w-0 gap-1.5">
          <SituacaoCaptura estado={estado} />
          <AcaoSugerida estado={estado} />
          <dl className="flex flex-wrap gap-x-5 gap-y-1">
            <UltimaProxima estado={estado} />
          </dl>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link
            to={`/captura?empresa=${empresaId}`}
            className="inline-flex items-center gap-1.5 text-sm text-marca hover:underline"
          >
            <History className="size-4" aria-hidden />
            Ver histórico
          </Link>
          {podeNaEmpresa(usuario, empresaId, 'documentos.captura.executar') && (
            <BuscarAgora estado={estado} tamanho="sm" />
          )}
        </div>
      </div>
      {estado.suspeitaOutroConsumidor && <AvisoOutroConsumidor />}
    </section>
  );
}
